import type React from "react";
import { Check, CircleAlert } from "lucide-react";
import { badgeVariants } from "../../atoms/Badge/Badge.js";
import { cn } from "../../lib/utils.js";
import type { StepperVariant } from "./Stepper.types.js";

/* ---------------- StepperMarker (Stepper's step disc) ----------------
   Internal to Stepper (not in the barrel). The disc comes from its size and
   the colour from Stepper's own status paint: Badge tone cannot express
   `current`, because that is the teal selection state (C7). */

/** A step's state. `static` is a step in a numbered list that tracks no
 *  progress (Stepper `mode="static"`). */
export type StepStatus =
  | "complete"
  | "current"
  | "upcoming"
  | "error"
  | "static";

/** `md` is the 28px marker. `sm` is the one 20px step/index disc. */
export type StepperSize = "sm" | "md";

const DISC: Record<StepperSize, string> = {
  md: "flex items-center justify-center w-7 h-7 rounded-full border text-xs font-semibold shrink-0 transition-colors",
  // The DS has one 20px disc: Badge shape="circle" size="sm" (badge-shape-size).
  sm: cn(
    badgeVariants({ shape: "circle", size: "sm" }),
    "shrink-0 transition-colors",
  ),
};

const PAINT: Record<StepStatus, string> = {
  complete: "bg-pb-green border-pb-green text-pb-on-accent",
  current: "border-pb-teal text-pb-teal-text",
  upcoming: "border-pb-border text-pb-text-muted",
  error: "border-pb-red text-pb-red-text",
  static: "border-pb-border text-pb-text-muted",
};

/* The recipe's px-1.5 is sized for one digit. The sm 12px glyph takes that
   padding back (-mx-1), so the disc stays 20px square. */
const GLYPH: Record<StepperSize, string> = {
  md: "w-4 h-4",
  sm: "w-3 h-3 -mx-1",
};

/* Stepper `tone="info"`: the same states in the info tone. An upcoming disc
   keeps the card fill, so a connector never shows through it. */
const INFO_PAINT: Record<StepStatus, string> = {
  complete: "bg-pb-cyan-soft border-pb-cyan/40 text-pb-cyan-text",
  current: "bg-pb-cyan border-pb-cyan text-pb-on-accent",
  upcoming: "bg-pb-card border-pb-border text-pb-text-muted",
  error: PAINT.error,
  static: PAINT.static,
};

/* Stepper `variant="swatch"`: a 28px disc filled with the step's own colour;
   the current step carries the one selection glow. */
const SWATCH =
  "flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold text-pb-on-accent shrink-0";

export interface StepperMarkerProps {
  status: StepStatus;
  /** Zero-based position; the marker shows `index + 1` unless `marker`. */
  index: number;
  size: StepperSize;
  marker?: React.ReactNode;
  color?: string | undefined;
  variant?: StepperVariant;
  tone?: "info" | undefined;
}

export function StepperMarker({
  status,
  index,
  size,
  marker,
  color,
  variant = "numbered",
  tone,
}: StepperMarkerProps) {
  const text = marker ?? index + 1;
  if (variant === "swatch") {
    return (
      <span
        aria-hidden="true"
        className={cn(SWATCH, status === "current" && "shadow-glow-selected")}
        style={color ? { backgroundColor: color } : undefined}
      >
        {text}
      </span>
    );
  }
  const paint = tone === "info" ? INFO_PAINT : PAINT;
  return (
    <span aria-hidden="true" className={cn(DISC[size], paint[status])}>
      {status === "complete" ? (
        <Check className={GLYPH[size]} strokeWidth={3} />
      ) : status === "error" ? (
        <CircleAlert className={GLYPH[size]} />
      ) : (
        text
      )}
    </span>
  );
}
